import { useResponsive } from '@hooks/useResponsive';
import useInternalRouteMapping from '@hooks/useInternalRouteMapping.hook';
import { useFormContext } from 'react-hook-form';
import CardSelect, { CARD_SELECT_VARIANTS } from '@ui/atoms/CardSelect';
import Stack from '@ui/layout/Stack';
import Cluster from '@ui/layout/Cluster';
import Text from '@ui/atoms/Text';
import { FormattedMessage } from 'react-intl';
import EpisodeEditTooltip from '@app/molecules/EpisodeEditTooltip';
import { EPISODE_PRIVACY } from '@/utils/constants';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import UiKitTooltip from '@ui/atoms/UiKitTooltip';
import { PRICING } from '@/utils/pricing';
import Link from '@ui/atoms/Link';
import styled from 'styled-components';
import { CardSelectListController } from '@ui/molecules/CardSelectList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const StyledCardSelectListController = styled(CardSelectListController)`
    flex-direction: column;

    ${({ theme }) => theme.mediaQueries.desktopLargeAndUp} {
        flex-direction: row;
    }
`;

const CardSelectIcon = styled(FontAwesomeIcon)`
    color: var(--primary);
    width: 1.5rem;
    height: 1.5rem;
`;

const UpgradeLink = styled(Text)`
    color: var(--white);
    font-weight: var(--fw-semibold);
    text-decoration: underline;
`;

const FlexTooltip = styled(UiKitTooltip)`
    flex: 1;
    height: auto;
    width: auto;
`;

const UpgradeForUnlistedOptionTooltip = ({ children, upgradeLink, passthrough }) => (
    <FlexTooltip
        variant="dark"
        content={
            <Stack $gap="0.5rem" $align="center">
                <Text fontWeight="--fw-semibold">
                    <FormattedMessage
                        defaultMessage="Pour publier votre épisode en non-listé, vous devez bénéficier d'une offre {offer}."
                        values={{
                            offer: (
                                <Text
                                    as="span"
                                    color="--white"
                                    fontWeight="--fw-bold"
                                    textTransform="capitalize"
                                >
                                    {PRICING.BOOST}
                                </Text>
                            ),
                        }}
                    />
                </Text>
                <UpgradeLink as={Link} to={upgradeLink}>
                    <FormattedMessage defaultMessage="Mettre à jour mon offre" />
                </UpgradeLink>
            </Stack>
        }
        passthrough={passthrough}
        position="top"
    >
        {children}
    </FlexTooltip>
);

const EpisodeEditFormPrivacyPrivacyField = ({ isUnlistedAllowed = false }) => {
    const { isDesktopLarge } = useResponsive();
    const internalRouteMapping = useInternalRouteMapping();
    const { control } = useFormContext();

    const cardSelectVariant = isDesktopLarge
        ? CARD_SELECT_VARIANTS.LARGE
        : CARD_SELECT_VARIANTS.SMALL;

    return (
        <Stack $gap="0.5rem">
            <Cluster $gap="0.25rem" $align="center">
                <Text variant="bodyM" fontWeight="--fw-semibold">
                    <FormattedMessage defaultMessage="Visibilité" />
                </Text>
                <EpisodeEditTooltip>
                    <FormattedMessage defaultMessage="Vous pouvez gérer la confidentialité de votre épisode et savoir ainsi quelles seront les personnes pouvant le voir." />
                </EpisodeEditTooltip>
            </Cluster>
            <StyledCardSelectListController name="privacy.privacy" control={control}>
                <CardSelect
                    value={EPISODE_PRIVACY.PUBLIC}
                    variant={cardSelectVariant}
                    title={<FormattedMessage defaultMessage="Public" />}
                    description={
                        <FormattedMessage defaultMessage="Votre épisode sera visible par tout le monde." />
                    }
                    icon={<CardSelectIcon icon={icon({ name: 'eye', style: 'duotone' })} />}
                />
                <CardSelect
                    value={EPISODE_PRIVACY.PRIVATE}
                    variant={cardSelectVariant}
                    title={<FormattedMessage defaultMessage="Privé" />}
                    description={
                        <FormattedMessage defaultMessage="Vous seul pourrez accéder à votre épisode." />
                    }
                    icon={<CardSelectIcon icon={icon({ name: 'eye-slash', style: 'duotone' })} />}
                />
                <CardSelect
                    value={EPISODE_PRIVACY.UNLISTED}
                    variant={cardSelectVariant}
                    title={<FormattedMessage defaultMessage="Non-listé" />}
                    description={
                        <FormattedMessage defaultMessage="Votre épisode sera accessible aux personnes disposant du lien." />
                    }
                    icon={<CardSelectIcon icon={icon({ name: 'link', style: 'duotone' })} />}
                    isDisabled={!isUnlistedAllowed}
                    tooltip={
                        <UpgradeForUnlistedOptionTooltip
                            upgradeLink={internalRouteMapping.link('user.edit.offers')}
                            passthrough={isUnlistedAllowed}
                        />
                    }
                />
            </StyledCardSelectListController>
        </Stack>
    );
};

export default EpisodeEditFormPrivacyPrivacyField;
